import { Link, Typography } from '@mui/material'
import { Fragment, useState } from 'react'
import { AssessmentAttempt, Submission } from '../types'
import {
  AssessmentStatus,
  SessionStorageKeys,
  SubmissionStatus,
} from '../enums'
import ZIcon from '../components/ZIcon'
import ZBottomContainer from '../components/ZBottomContainer'
import ZButton from '../components/ZButton'
import SkipAppropriatenessAssessment from './SkipAppropriatenessAssessment'
import ZScreenContainer from '../components/ZScreenContainer'
import {
  AppropriatenessAssessmentPassIcon,
  AppropriatenessAssessmentFailIcon,
} from '../assets/icons'
import { canReturnToApp, returnToApp } from '../util/notifyHost'
import {
  AssessmentFailureCopy,
  CopyParagraph,
  buildAssessmentFailureCopy,
  minutesUntil,
} from '../util/assessmentFeedback'

const AppropriatenessAssessmentCompleted = ({
  assessment,
  submission,
  onDecision,
  knowledgeHubUrl,
  deepLinkURI,
}: {
  assessment: AssessmentAttempt
  submission: Submission
  onDecision: (retry: boolean) => void
  knowledgeHubUrl: string
  deepLinkURI?: string
}) => {
  const [skipRetry, setSkipRetry] = useState<boolean>()

  const canReturn = canReturnToApp(deepLinkURI)

  const authCode = sessionStorage.getItem(SessionStorageKeys.AuthCode)

  if (skipRetry) {
    return <SkipAppropriatenessAssessment onDecision={onDecision} />
  }

  const returnToAppButton = canReturn ? (
    <ZBottomContainer>
      <ZButton onClick={() => returnToApp(authCode!, deepLinkURI)}>
        Return to the app
      </ZButton>
    </ZBottomContainer>
  ) : null

  const renderParagraphs = (paragraphs: CopyParagraph[]) => (
    <Typography>
      {paragraphs.map((nodes, pIdx) => (
        <Fragment key={pIdx}>
          {pIdx > 0 && (
            <>
              <br />
              <br />
            </>
          )}
          {nodes.map((node, nIdx) =>
            typeof node === 'string' ? (
              <Fragment key={nIdx}>{node}</Fragment>
            ) : (
              <Link
                key={nIdx}
                href={knowledgeHubUrl}
                underline="none"
                target="_blank"
              >
                {node.link}
              </Link>
            ),
          )}
        </Fragment>
      ))}
    </Typography>
  )

  const failureScreen = (copy: AssessmentFailureCopy) => (
    <ZScreenContainer>
      <ZIcon
        Icon={AppropriatenessAssessmentFailIcon}
        title="Appropriateness assessment fail icon"
      />

      {renderParagraphs(copy.paragraphs)}

      {copy.primaryButton === 'retry' ? (
        <ZBottomContainer>
          <ZButton onClick={() => onDecision(true)}>
            Complete a new assessment
          </ZButton>

          {copy.showLeaveButton && (
            <ZButton onClick={() => setSkipRetry(true)}>
              I wish to leave this investment journey
            </ZButton>
          )}
        </ZBottomContainer>
      ) : (
        returnToAppButton
      )}
    </ZScreenContainer>
  )

  if (submission.status === SubmissionStatus.Failed) {
    return failureScreen(
      buildAssessmentFailureCopy({
        terminal: true,
        canRetryNow: false,
        attempt: assessment.attempt,
        maxAttempts: submission.assessmentMaxAttempts,
        delaysHours: submission.assessmentAttemptDelaysHours,
        canReturnToApp: canReturn,
      }),
    )
  }

  // A PASSED submission routes to the acceptance step, never here.
  if (
    assessment.status === AssessmentStatus.Passed ||
    submission.status === SubmissionStatus.CoolingOff
  ) {
    return (
      <ZScreenContainer>
        <ZIcon
          Icon={AppropriatenessAssessmentPassIcon}
          title="Appropriateness assessment pass icon"
        />

        <Typography textAlign="center">
          Thank you for completing the assessment.
        </Typography>

        <Typography textAlign="center">
          Your submission is now being processed, and the status will be
          available once complete.
        </Typography>

        {canReturn ? (
          <>
            <Typography textAlign="center">
              Please press the button below to return to the app.
            </Typography>
            {returnToAppButton}
          </>
        ) : (
          <Typography>
            You can close this page and return to the app.
          </Typography>
        )}
      </ZScreenContainer>
    )
  }

  const nextAttemptAt = assessment.nextAttemptAt
    ? new Date(assessment.nextAttemptAt)
    : null
  const canRetryNow = !nextAttemptAt || nextAttemptAt.getTime() <= Date.now()
  const minutesUntilRetry = nextAttemptAt
    ? minutesUntil(nextAttemptAt, Date.now())
    : 0

  return failureScreen(
    buildAssessmentFailureCopy({
      terminal: false,
      canRetryNow,
      attempt: assessment.attempt,
      maxAttempts: submission.assessmentMaxAttempts,
      delaysHours: submission.assessmentAttemptDelaysHours,
      canReturnToApp: canReturn,
      minutesUntilRetry,
    }),
  )
}

export default AppropriatenessAssessmentCompleted
